import {
  SESSION_INVALIDATING,
  SESSION_SAFE_UPDATE,
} from './trainingTypeListSync';

export { SESSION_INVALIDATING, SESSION_SAFE_UPDATE };

export const OTK_COMPANY_CERT_STATE_UPDATED_EVENT = 'otk_company_cert_state:updated';

export const SESSION_TOMBSTONE = Object.freeze({ __sessionTombstone: true });

export const OTK_COMPANY_CERT_KIND = Object.freeze({
  PARENT_CREATED: 'parent-created',
  PARENT_UPDATED: 'parent-updated',
  PARENT_DELETED: 'parent-deleted',
  PARENT_ACTIVATED: 'parent-activated',
  PARENT_DEACTIVATED: 'parent-deactivated',
  RECORD_CREATED: 'record-created',
  RECORD_UPDATED: 'record-updated',
  RECORD_DELETED: 'record-deleted',
  RECORD_FILE_CHANGED: 'record-file-changed',
});

const MEMBERSHIP_FIELDS = new Set([
  'deactivation_date',
  'certificate_status',
  'custom_doc_status',
  'custom_company_status',
  'custom_partner_status',
  'company.validity',
  'custom_expiration',
  'state',
  'custom_start_of_validity',
  'custom_end_of_validity',
  'start_of_validity',
  'end_of_validity',
  'company_id',
  'company.name',
  'current_record',
  '_children',
]);

const SEARCHABLE_FIELDS = new Set([
  'name',
  'description',
  'cert_description',
  'company.name',
  'responsible.name',
  'responsible_employee_id',
]);

export const OTK_COMPANY_CERT_FILTER_REMAP = Object.freeze({
  cert_description: 'description',
  description: 'record_description',
  custom_partner_status: 'custom_company_status',
  custom_doc_status: 'certificate_status',
});

export const OTK_COMPANY_CERT_SORTER_REMAP = Object.freeze({
  cert_description: 'description',
  description: 'record_description',
  custom_partner_status: 'custom_company_status',
  custom_doc_status: 'certificate_status',
});

export const OTK_COMPANY_CERT_UNSORTABLE_FIELDS = Object.freeze([
  'custom_expiration',
  'required_attachment',
]);

function scalar(value) {
  if (value == null) return '';
  if (typeof value === 'object') {
    if (value.id != null) return String(value.id);
    return '';
  }
  return String(value);
}

function hasActiveValue(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.some((item) => hasActiveValue(item));
  return String(value).trim() !== '';
}

function remapField(field) {
  if (field == null || field === '') return field;
  return OTK_COMPANY_CERT_FILTER_REMAP[field] || field;
}

function expandFields(fields) {
  const expanded = new Set();
  (Array.isArray(fields) ? fields : []).forEach((field) => {
    if (field == null || field === '') return;
    expanded.add(field);
    expanded.add(remapField(field));
  });
  return expanded;
}

export function resolveOtkCertTreeAuditRow(rowData) {
  return rowData && typeof rowData === 'object' ? rowData : {};
}

export function otkCertTreeRowKey(row, asChild = false) {
  if (!row || row.id == null) return '';
  const child = asChild || (row.certificate_id != null && !Object.prototype.hasOwnProperty.call(row, '_children'));
  return child ? `rec-${row.id}` : `cert-${row.id}`;
}

export function decorateOtkCertTreeRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    if (!row || typeof row !== 'object') return row;
    const children = Array.isArray(row._children)
      ? row._children.map((child) => (child && typeof child === 'object'
        ? { ...child, _rowKey: otkCertTreeRowKey(child, true) }
        : child))
      : [];
    return { ...row, _rowKey: otkCertTreeRowKey(row, false), _children: children };
  });
}

export function mapOtkCompanyCertSorterField(field) {
  if (!field) return field;
  if (OTK_COMPANY_CERT_UNSORTABLE_FIELDS.includes(field)) return null;
  return OTK_COMPANY_CERT_SORTER_REMAP[field] || field;
}

export function mapOtkCompanyCertHeaderFiltersToQuery(headerFilters) {
  const extra = {};
  (Array.isArray(headerFilters) ? headerFilters : []).forEach((f) => {
    if (f?.field == null || f.value == null || String(f.value).trim() === '') return;
    const val = String(f.value).trim();
    if (f.field === 'custom_expiration' && val === 'Všetko') return;
    extra[`filter_${remapField(f.field)}`] = val;
  });
  return extra;
}

export function emitOtkCompanyCertStateMutation(detail) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(OTK_COMPANY_CERT_STATE_UPDATED_EVENT, { detail: detail || {} }));
}

function isDefaultIdAscSorter(sorter) {
  const field = sorter?.field || sorter?.column;
  const dir = sorter?.dir || 'asc';
  return field === 'id' && dir === 'asc';
}

export function isSessionTombstone(value) {
  return value === SESSION_TOMBSTONE || value?.__sessionTombstone === true;
}

export function collectOtkCompanyCertChangedFields(previous, next, options = {}) {
  const prev = previous && typeof previous === 'object' ? previous : {};
  const nxt = next && typeof next === 'object' ? next : {};
  const changed = [];
  [
    'name',
    'description',
    'cert_description',
    'company_id',
    'deactivation_date',
    'certificate_status',
    'custom_doc_status',
    'custom_company_status',
    'custom_partner_status',
    'custom_expiration',
    'state',
    'custom_start_of_validity',
    'custom_end_of_validity',
    'responsible_employee_id',
    'required_attachment',
    'protocol',
  ].forEach((key) => {
    if (scalar(prev[key]) !== scalar(nxt[key])) changed.push(key);
  });
  if (scalar(prev.company?.name) !== scalar(nxt.company?.name)) changed.push('company.name');
  if (scalar(prev.responsible?.name) !== scalar(nxt.responsible?.name)) changed.push('responsible.name');
  const prevKids = Array.isArray(prev._children) ? prev._children.map((child) => child?.id).join(',') : '';
  const nextKids = Array.isArray(nxt._children) ? nxt._children.map((child) => child?.id).join(',') : '';
  if (prevKids !== nextKids) {
    changed.push('_children');
    changed.push('current_record');
  }
  const prevFirst = Array.isArray(prev._children) ? prev._children[0] : null;
  const nextFirst = Array.isArray(nxt._children) ? nxt._children[0] : null;
  if (scalar(prevFirst?.description) !== scalar(nextFirst?.description)) changed.push('description');
  if (scalar(prevFirst?.start_of_validity) !== scalar(nextFirst?.start_of_validity)) changed.push('custom_start_of_validity');
  if (scalar(prevFirst?.end_of_validity) !== scalar(nextFirst?.end_of_validity)) changed.push('custom_end_of_validity');
  if (scalar(prevFirst?.state) !== scalar(nextFirst?.state)) changed.push('state');
  const fileSig = (row) => (Array.isArray(row?.files) ? row.files.map((file) => file?.id).join(',') : '');
  if (fileSig(prevFirst) !== fileSig(nextFirst)) changed.push('protocol');
  if (options.implicitAudit) {
    changed.push('updated_at');
    changed.push('updated_by_user');
  }
  return Array.from(new Set(changed));
}

export function applyOtkCertPagedMutationOverlay(response, overlay) {
  if (!response || typeof response !== 'object' || !Array.isArray(response.data)) return response;
  if (!overlay || typeof overlay.get !== 'function' || overlay.size === 0) return response;
  return {
    ...response,
    data: response.data.reduce((acc, row) => {
      if (!row || row.id == null) {
        acc.push(row);
        return acc;
      }
      const over = overlay.get(String(row.id));
      if (isSessionTombstone(over)) return acc;
      acc.push(over || row);
      return acc;
    }, []),
  };
}

export function classifyOtkCompanyCertStateMutation(event, session = {}) {
  const detail = event?.detail;
  if (!detail || typeof detail !== 'object') return SESSION_INVALIDATING;
  const kind = detail.kind;
  const certificateId = detail.certificateId ?? detail.id;
  if (certificateId == null || certificateId === '') return SESSION_INVALIDATING;
  if (typeof session.isRowLoaded === 'function' && !session.isRowLoaded(certificateId)) {
    if (kind === OTK_COMPANY_CERT_KIND.PARENT_UPDATED || kind === OTK_COMPANY_CERT_KIND.RECORD_UPDATED || kind === OTK_COMPANY_CERT_KIND.RECORD_FILE_CHANGED) {
      return SESSION_INVALIDATING;
    }
  }

  if (
    kind === OTK_COMPANY_CERT_KIND.PARENT_CREATED
    || kind === OTK_COMPANY_CERT_KIND.PARENT_DELETED
    || kind === OTK_COMPANY_CERT_KIND.PARENT_ACTIVATED
    || kind === OTK_COMPANY_CERT_KIND.PARENT_DEACTIVATED
    || kind === OTK_COMPANY_CERT_KIND.RECORD_CREATED
    || kind === OTK_COMPANY_CERT_KIND.RECORD_DELETED
  ) {
    return SESSION_INVALIDATING;
  }

  const changed = expandFields(Array.isArray(detail.changedFields) ? detail.changedFields : []);
  if (String(session.toolbarSearch ?? '').trim() !== '') {
    for (const field of changed) {
      if (SEARCHABLE_FIELDS.has(field) || SEARCHABLE_FIELDS.has(remapField(field))) return SESSION_INVALIDATING;
    }
  }

  if (kind === OTK_COMPANY_CERT_KIND.RECORD_FILE_CHANGED) {
    const filters = Array.isArray(session.activeHeaderFilters) ? session.activeHeaderFilters : [];
    const sorters = Array.isArray(session.activeSorters) ? session.activeSorters : [];
    const protocolActive = filters.some((f) => remapField(f?.field) === 'protocol' && hasActiveValue(f?.value))
      || sorters.some((s) => mapOtkCompanyCertSorterField(s?.field || s?.column) === 'protocol' && !isDefaultIdAscSorter(s));
    return protocolActive ? SESSION_INVALIDATING : SESSION_SAFE_UPDATE;
  }

  if (kind === OTK_COMPANY_CERT_KIND.RECORD_UPDATED) {
    for (const field of changed) {
      if (MEMBERSHIP_FIELDS.has(field) || MEMBERSHIP_FIELDS.has(remapField(field))) return SESSION_INVALIDATING;
    }
    const filters = Array.isArray(session.activeHeaderFilters) ? session.activeHeaderFilters : [];
    const sorters = Array.isArray(session.activeSorters) ? session.activeSorters : [];
    for (const filter of filters) {
      if (!filter?.field || !hasActiveValue(filter.value)) continue;
      if (expandFields([filter.field]).some((alias) => changed.has(alias) || changed.has(remapField(alias)))) {
        return SESSION_INVALIDATING;
      }
    }
    for (const sorter of sorters) {
      if (isDefaultIdAscSorter(sorter)) continue;
      const mapped = mapOtkCompanyCertSorterField(sorter?.field || sorter?.column);
      if (mapped && (changed.has(mapped) || changed.has(sorter?.field))) return SESSION_INVALIDATING;
    }
    return SESSION_SAFE_UPDATE;
  }

  if (kind === OTK_COMPANY_CERT_KIND.PARENT_UPDATED) {
    for (const field of changed) {
      if (MEMBERSHIP_FIELDS.has(field) || MEMBERSHIP_FIELDS.has(remapField(field))) return SESSION_INVALIDATING;
    }
    const filters = Array.isArray(session.activeHeaderFilters) ? session.activeHeaderFilters : [];
    for (const filter of filters) {
      if (!filter?.field || !hasActiveValue(filter.value)) continue;
      if (expandFields([filter.field]).some((alias) => changed.has(alias) || changed.has(remapField(alias)))) {
        return SESSION_INVALIDATING;
      }
    }
    const sorters = Array.isArray(session.activeSorters) ? session.activeSorters : [];
    for (const sorter of sorters) {
      if (isDefaultIdAscSorter(sorter)) continue;
      const mapped = mapOtkCompanyCertSorterField(sorter?.field || sorter?.column);
      if (mapped && (changed.has(mapped) || changed.has(sorter?.field))) return SESSION_INVALIDATING;
    }
    if (typeof session.isRowLoaded === 'function' && !session.isRowLoaded(certificateId)) return SESSION_INVALIDATING;
    return SESSION_SAFE_UPDATE;
  }

  return SESSION_INVALIDATING;
}
